import React from "react";
import { TableBody } from "@mui/material";
import {
  formatHeadroomTableData,
  sortHeadroomRows,
  formatHeadroomCombinedTableData,
  sortHeadroomCombinedRows,
  HeadroomTableSegment,
} from "../../utils/headroomTableUtils";
import { AnalysisDriverType } from "../../types/types";
import { HeadroomFinalResponse } from "@/graphql/types/graphql";
import EmptyTableBody from "./EmptyTableBody";
import SkeletonHeader from "./SkeletonHeader";
import HeadroomTableBodyRows from "./HeadroomTableBodyRows";

interface HeadroomFinalTableBodyProps {
  /** Array of table segments */
  segments: HeadroomTableSegment[];
  /** Headroom response data */
  data: HeadroomFinalResponse[] | undefined;
  /** Mode for driver metrics */
  driverMode: AnalysisDriverType;
  /** Column to sort by */
  sortColumn: string;
  /** Sort direction */
  sortDirection: "asc" | "desc";
  /** Indicates if there is one data and one manual segment */
  hasOneDataAndOneManualSegment: boolean;
  /** Indicates if skeleton loading state should be displayed */
  isSkeleton: boolean;
}

const HeadroomFinalResultsTableBody: React.FC<HeadroomFinalTableBodyProps> = ({
  segments,
  data,
  driverMode,
  sortColumn,
  sortDirection,
  hasOneDataAndOneManualSegment,
  isSkeleton,
}) => {
  const isManualOnly = segments.length === 1 && segments.every((seg) => seg.method === "MANUAL");
  const isDataOnly = segments.length === 1 && segments.every((seg) => seg.method === "DATA");
  const isTwoSameMethod = segments.length === 2 && segments.every((seg) => seg.method === segments[0].method);

  if (isSkeleton) {
    const skeletonColumnCount = isManualOnly || isDataOnly || hasOneDataAndOneManualSegment ? 12 : 13;
    return <SkeletonHeader columnCount={skeletonColumnCount} />;
  }

  if (isManualOnly || isDataOnly) {
    const { displaySegments, rows, isDataOnly: isDataOnlyFromFormat } = formatHeadroomTableData(segments, data);
    const sortedRows = sortHeadroomRows(rows, sortColumn, sortDirection, isDataOnlyFromFormat);
    return (
      <TableBody>
        {displaySegments.length > 0 ? (
          <HeadroomTableBodyRows
            rows={sortedRows}
            segments={segments}
            driverMode={driverMode}
            isManualOnly={isManualOnly}
            isDataOnly={isDataOnly}
          />
        ) : (
          <EmptyTableBody colSpan={15} />
        )}
      </TableBody>
    );
  }

  if (hasOneDataAndOneManualSegment) {
    const { combinedRows } = formatHeadroomCombinedTableData(segments, data);
    const sortedRows = sortHeadroomCombinedRows(combinedRows, sortColumn, sortDirection);
    return (
      <TableBody>
        {sortedRows.length > 0 ? (
          <HeadroomTableBodyRows rows={sortedRows} driverMode={driverMode} hasOneDataAndOneManualSegment />
        ) : (
          <EmptyTableBody colSpan={15} />
        )}
      </TableBody>
    );
  }

  if (isTwoSameMethod) {
    const { displaySegments, rows, isDataOnly: isDataOnlyFromFormat } = formatHeadroomTableData(segments, data);
    const sortedRows = sortHeadroomRows(rows, sortColumn, sortDirection, isDataOnlyFromFormat);
    return (
      <TableBody>
        {displaySegments.length > 0 ? (
          <HeadroomTableBodyRows rows={sortedRows} segments={segments} driverMode={driverMode} isTwoSameMethod />
        ) : (
          <EmptyTableBody colSpan={15} />
        )}
      </TableBody>
    );
  }

  const { displaySegments, rows, isDataOnly: isDataOnlyFromFormat } = formatHeadroomTableData(segments, data);
  const sortedRows = sortHeadroomRows(rows, sortColumn, sortDirection, isDataOnlyFromFormat);
  return (
    <TableBody>
      {displaySegments.length > 0 ? (
        <HeadroomTableBodyRows rows={sortedRows} segments={segments} driverMode={driverMode} />
      ) : (
        <EmptyTableBody colSpan={15} />
      )}
    </TableBody>
  );
};

export default HeadroomFinalResultsTableBody;
